import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { LanguageEnum } from "@/types/enums";

/** 支持的语言列表（顺序即 hreflang 输出顺序）；英文为默认根路径语言 */
export const SUPPORTED_LANGUAGES = [LanguageEnum.EN, LanguageEnum.ZH, LanguageEnum.FR] as const;

/** 语言对应的 URL 前缀（英文为默认语言，无前缀） */
export const LOCALE_PREFIXES: Record<LanguageEnum, string> = {
  [LanguageEnum.EN]: "",
  [LanguageEnum.ZH]: "/zh",
  [LanguageEnum.FR]: "/fr",
};

export const HTML_LANG: Record<LanguageEnum, string> = {
  [LanguageEnum.ZH]: "zh-CN",
  [LanguageEnum.EN]: "en-CA",
  [LanguageEnum.FR]: "fr-CA",
};

export const HREFLANG: Record<LanguageEnum, string> = {
  [LanguageEnum.EN]: "en-CA",
  [LanguageEnum.ZH]: "zh-CN",
  [LanguageEnum.FR]: "fr-CA",
};

export const OG_LOCALE: Record<LanguageEnum, string> = {
  [LanguageEnum.EN]: "en_CA",
  [LanguageEnum.ZH]: "zh_CN",
  [LanguageEnum.FR]: "fr_CA",
};

export function normalizeLanguage(lng?: string): LanguageEnum {
  const base = (lng || "").split("-")[0] as LanguageEnum;
  return (SUPPORTED_LANGUAGES as readonly LanguageEnum[]).includes(base) ? base : LanguageEnum.EN;
}

export function getLangFromPathname(pathname: string): LanguageEnum {
  const match = pathname.match(/^\/(zh|fr)(\/|$)/);
  if (match?.[1] === "zh") return LanguageEnum.ZH;
  if (match?.[1] === "fr") return LanguageEnum.FR;
  return LanguageEnum.EN;
}

export function stripLocalePrefix(pathname: string): string {
  const match = pathname.match(/^\/(zh|fr)(\/|$)/);
  if (!match) return pathname;
  const rest = pathname.slice(match[1].length + 1);
  return rest === "" || rest === "/" ? "/" : rest;
}

export function localizePath(path: string, lng: string): string {
  if (!path.startsWith("/")) return path;
  const lang = normalizeLanguage(lng);
  const prefix = LOCALE_PREFIXES[lang];
  if (!prefix) return path;
  if (path === prefix || path.startsWith(`${prefix}/`) || path.startsWith(`${prefix}?`)) return path;
  if (path === "/") return prefix;
  return `${prefix}${path}`;
}

export function useLocalePath() {
  const { i18n } = useTranslation();
  const lang = normalizeLanguage(i18n.language);
  return useCallback((path: string) => localizePath(path, lang), [lang]);
}
